import { useState, useEffect, useMemo, Fragment } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ShieldCheck,
  Plus,
  X,
  ShieldAlert,
  Info,
  Loader2,
  Save,
  Edit,
  Trash2,
  Eye,
  PlusCircle,
  Pencil,
  ClipboardCheck,
  CheckCircle,
  ShoppingCart,
  Factory,
  Beaker,
  Container,
  Settings,
  AlertCircle,
  Upload,
  Download,
  FileSearch,
  CreditCard,
  Layers,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import type { Role } from "@/shared/schemas";
import {
  SortableHeader,
  useTableSort,
} from "@/components/shared/SortableHeader";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useAuthStore } from "@/stores/auth.store";
import { exportToExcel } from "@/lib/excel.utils";

// ── Permission Actions ─────────────────────────────────────────────────────
const ACTUAL_ACTIONS = [
  { id: "view", label: "Xem", icon: Eye, color: "text-blue-500" },
  { id: "create", label: "Thêm", icon: PlusCircle, color: "text-emerald-500" },
  { id: "update", label: "Sửa", icon: Pencil, color: "text-amber-500" },
  { id: "delete", label: "Xóa", icon: Trash2, color: "text-rose-500" },
  { id: "import", label: "Import", icon: Upload, color: "text-indigo-400" },
  { id: "export", label: "Export", icon: Download, color: "text-emerald-400" },
  { id: "review", label: "Review", icon: FileSearch, color: "text-purple-500" },
  { id: "approve", label: "Duyệt", icon: CheckCircle, color: "text-rose-700" },
];
const PERM_ACTIONS = [
  { id: "all", label: "Tất cả", icon: ShieldCheck, color: "text-indigo-500" },
  ...ACTUAL_ACTIONS,
];

// ── Module Groups ──────────────────────────────────────────────────────────
const MODULE_GROUPS = [
  {
    group: "Kinh Doanh",
    icon: ShoppingCart,
    color: "bg-blue-500",
    modules: [
      { id: "customers", name: "Khách hàng" },
      { id: "quotations", name: "Báo giá" },
      { id: "orders", name: "Đơn hàng" },
      { id: "deliveries", name: "Giao hàng" },
      { id: "products", name: "Sản phẩm" },
      { id: "product-types", name: "Loại sản phẩm" },
    ],
  },
  {
    group: "Kế Hoạch & Mua Hàng",
    icon: ClipboardCheck,
    color: "bg-indigo-500",
    modules: [
      { id: "production-orders", name: "Lệnh sản xuất" },
      { id: "material-planning", name: "KH Vật tư" },
      { id: "process-planning", name: "KH Công đoạn" },
      { id: "purchasing", name: "ĐH Vật tư" },
      { id: "outsource-orders", name: "ĐH Gia công" },
      { id: "vendors", name: "Nhà cung cấp" },
    ],
  },
  {
    group: "Tài Chính",
    icon: CreditCard,
    color: "bg-emerald-500",
    modules: [
      { id: "receipts", name: "Thu chi" },
      { id: "debts", name: "Công nợ KH" },
    ],
  },
  {
    group: "Sản Xuất",
    icon: Factory,
    color: "bg-orange-500",
    modules: [
      { id: "machine-logs", name: "Nhật ký máy" },
      { id: "wip-transfer", name: "Bàn giao BTP" },
    ],
  },
  {
    group: "Kỹ Thuật",
    icon: Layers,
    color: "bg-purple-500",
    modules: [
      { id: "specs", name: "Tiêu chuẩn SP" },
      { id: "layouts", name: "Bình bản" },
      { id: "molds", name: "Khuôn" },
      { id: "plates", name: "Bản in Kẽm" },
      { id: "ctp-logs", name: "Nhật ký CTP" },
    ],
  },
  {
    group: "Chất Lượng",
    icon: Beaker,
    color: "bg-green-500",
    modules: [
      { id: "qc-materials", name: "QC NVL" },
      { id: "qc-process", name: "QC công đoạn" },
      { id: "qc-final", name: "QC thành phẩm" },
      { id: "ncr", name: "NCR / CAPA" },
    ],
  },
  {
    group: "Kho",
    icon: Container,
    color: "bg-cyan-500",
    modules: [
      { id: "material-warehouse", name: "Kho VT" },
      { id: "fg-inventory", name: "Kho TP" },
      { id: "materials", name: "Vật tư" },
      { id: "material-types", name: "Loại vật tư" },
    ],
  },
  {
    group: "Hệ thống",
    icon: Settings,
    color: "bg-slate-600",
    modules: [
      { id: "company-info", name: "Công ty" },
      { id: "users", name: "Người dùng" },
      { id: "roles", name: "Phân quyền" },
      { id: "machines", name: "Thiết bị" },
      { id: "processes", name: "Quy trình SX" },
      { id: "workflows", name: "Quy trình WF" },
      { id: "configs", name: "Cấu hình" },
      { id: "reports", name: "Báo cáo & Thống kê" },
      { id: "trash", name: "Thùng rác" },
      { id: "feedback", name: "Góp ý & Hỗ trợ" },
    ],
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────
const hasModuleAllPerms = (actions: string[]) =>
  ACTUAL_ACTIONS.every((a) => actions.includes(a.id));

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT — Single-page Roles + Permissions Dashboard
// ═══════════════════════════════════════════════════════════════════════════
export default function RolesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useRoleAccess();

  // ── States ──
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // ── Fetch all roles ──
  const { data: allRolesData, isLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => trpcClient.roles.list.query() as Promise<Role[]>,
  });
  const allRoles: Role[] = allRolesData || [];

  // ── Role Search State & Filtering ──
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const filteredRoles = useMemo(() => {
    if (!roleSearchTerm.trim()) return allRoles;
    const term = roleSearchTerm.toLowerCase();
    return allRoles.filter(
      (r) =>
        r.name?.toLowerCase().includes(term) ||
        r.roleId?.toLowerCase().includes(term) ||
        r.description?.toLowerCase().includes(term),
    );
  }, [allRoles, roleSearchTerm]);

  // ── Auto-select first role if none selected ──
  useEffect(() => {
    if (!selectedRoleId && allRoles.length > 0) {
      setSelectedRoleId(allRoles[0].roleId);
    }
  }, [allRoles, selectedRoleId]);

  // ── Fetch detail for selected role ──
  const { data: roleData, isLoading: roleLoading } = useQuery({
    queryKey: ["role", selectedRoleId],
    queryFn: async () =>
      trpcClient.roles.getById.query({ id: selectedRoleId! }) as Promise<Role>,
    enabled: !!selectedRoleId,
  });

  const [role, setRole] = useState<Role | undefined>(roleData);
  useEffect(() => {
    if (roleData) {
      let perms = roleData.permissions || {};
      if (Array.isArray(perms)) {
        const newObj: any = {};
        if (perms.includes("*")) newObj["*"] = true;
        perms.forEach((p) => {
          if (p === "*") return;
          const [mod, act] = p.split(".");
          if (mod && act) {
            const normAct = act === "edit" ? "update" : act === "check" ? "review" : act;
            if (!newObj[mod])
              newObj[mod] = {
                actions: [],
                dataScope: "ALL",
                financialScope: "ALL",
              };
            if (!newObj[mod].actions.includes(normAct))
              newObj[mod].actions.push(normAct);
          }
        });
        perms = newObj;
      }
      setRole({ ...roleData, permissions: perms });
    }
  }, [roleData]);

  // ── Permission helpers ──
  // Xuất ma trận quyền của vai trò đang chọn
  const handleExportMatrix = () => {
    if (!role) return;
    const rows = MODULE_GROUPS.flatMap((g) =>
      g.modules.map((mod) => {
        const row: Record<string, string> = { group: g.group, module: mod.name };
        ACTUAL_ACTIONS.forEach((a) => {
          row[a.id] = hasPerm(mod.id, a.id) ? "x" : "";
        });
        const mp = role.permissions?.[mod.id];
        row.dataScope = mp?.dataScope || (role.permissions?.["*"] ? "ALL" : "NONE");
        row.financialScope = mp?.financialScope || (role.permissions?.["*"] ? "ALL" : "NONE");
        return row;
      }),
    );
    exportToExcel(
      rows,
      [
        { key: "group", label: "Nhóm", width: 22 },
        { key: "module", label: "Module", width: 26 },
        ...ACTUAL_ACTIONS.map((a) => ({ key: a.id, label: a.label, width: 9 })),
        { key: "dataScope", label: "Phạm vi dữ liệu", width: 16 },
        { key: "financialScope", label: "Phạm vi tiền/giá", width: 16 },
      ],
      `phan_quyen_${role.roleId}`,
    );
  };

  const hasPerm = (moduleId: string, actionId: string): boolean => {
    if (!role || !role.permissions) return false;
    if (role.permissions["*"]) return true;
    const modPerm = role.permissions[moduleId];
    if (!modPerm || !modPerm.actions) return false;
    if (actionId === "all") return hasModuleAllPerms(modPerm.actions);
    return modPerm.actions.includes(actionId);
  };

  const togglePerm = (moduleId: string, actionId: string) => {
    if (!role || !isEditing) return;
    if (role.permissions["*"]) {
      toast.error("Vui lòng tắt Full Access trước khi chỉnh quyền chi tiết");
      return;
    }

    const newPerms = { ...role.permissions };
    if (!newPerms[moduleId]) {
      newPerms[moduleId] = {
        actions: [],
        dataScope: "ALL",
        financialScope: "ALL",
      };
    }
    const modPerm = { ...newPerms[moduleId] };
    let actions = [...(modPerm.actions || [])];

    if (actionId === "all") {
      const allSet = hasModuleAllPerms(actions);
      if (allSet) actions = [];
      else actions = ACTUAL_ACTIONS.map((a) => a.id);
    } else {
      if (actions.includes(actionId))
        actions = actions.filter((a: string) => a !== actionId);
      else actions.push(actionId);
    }

    modPerm.actions = actions;
    if (actions.length > 0) {
      if (!modPerm.dataScope || modPerm.dataScope === "NONE") {
        modPerm.dataScope = "ALL";
      }
      if (!modPerm.financialScope || modPerm.financialScope === "NONE") {
        modPerm.financialScope = "ALL";
      }
    } else {
      modPerm.dataScope = "NONE";
      modPerm.financialScope = "NONE";
    }
    newPerms[moduleId] = modPerm;
    setRole({ ...role, permissions: newPerms });
  };

  const updateScope = (
    moduleId: string,
    type: "dataScope" | "financialScope",
    value: string,
  ) => {
    if (!role || !isEditing) return;
    if (role.permissions["*"]) {
      toast.error("Vai trò Toàn quyền không cần thiết lập giới hạn");
      return;
    }
    const newPerms = { ...role.permissions };
    if (!newPerms[moduleId]) {
      newPerms[moduleId] = {
        actions: [],
        dataScope: "ALL",
        financialScope: "ALL",
      };
    }
    newPerms[moduleId] = { ...newPerms[moduleId], [type]: value };
    setRole({ ...role, permissions: newPerms });
  };

  const handleFullAccessToggle = () => {
    if (!role) return;
    setRole({
      ...role,
      permissions: role.permissions["*"] ? {} : { "*": true },
    });
  };

  // ── Save ──
  const handleSave = async () => {
    if (!role || !selectedRoleId) return;
    setIsSaving(true);
    try {
      await trpcClient.roles.update.mutate({
        id: selectedRoleId,
        data: {
          name: role.name,
          description: role.description,
          permissions: role.permissions,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: ["role", selectedRoleId],
      });
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      // Sync current user's permissions immediately so WebApp reflects changes without re-login
      trpcClient.auth.me
        .query()
        .then((freshUser) => {
          if (freshUser && freshUser.id) {
            useAuthStore.getState().setUser(freshUser as any);
          }
        })
        .catch(() => {});
      toast.success("Đã lưu cấu hình phân quyền thành công.");
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi lưu");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!selectedRoleId) return;
    setIsDeleting(true);
    try {
      await trpcClient.roles.delete.mutate({ id: selectedRoleId });
      await queryClient.invalidateQueries({ queryKey: ["roles"] });

      toast.success("Đã xóa vai trò thành công.");
      setSelectedRoleId(null);
      setRole(undefined);
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // ── Stats ──
  const permCount = useMemo(() => {
    if (!role || !role.permissions) return 0;
    if (role.permissions["*"]) return "Full";
    return Object.values(role.permissions).reduce(
      (acc: number, mod: any) => acc + (mod.actions?.length || 0),
      0,
    );
  }, [role]);

  // ── Access Guard ──
  if (!hasPermission("roles", "view")) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-48px)] gap-3 text-slate-600">
        <ShieldAlert className="h-12 w-12 text-rose-500" />
        <h2 className="text-lg font-bold text-slate-800">Không có quyền truy cập</h2>
        <p className="text-sm text-slate-500">
          Bạn không được cấp quyền xem hoặc chỉnh sửa cấu hình Phân quyền hệ thống.
        </p>
      </div>
    );
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-48px)]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-[calc(100vh-48px)] overflow-hidden">
        {/* ── Top Bar ── */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-2">
            {role && (
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] px-2 py-0.5",
                  role.permissions?.["*"]
                    ? "bg-rose-50 text-rose-700 border-rose-200"
                    : "bg-indigo-50 text-indigo-700 border-indigo-200",
                )}
              >
                {role.permissions?.["*"] ? "ADMIN" : "CUSTOM"}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setIsEditing(false);
                    if (roleData) setRole(roleData);
                  }}
                >
                  <X className="mr-1 h-3 w-3" /> Hủy
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <Save className="mr-1 h-3 w-3" />
                  )}
                  Lưu
                </Button>
              </>
            ) : (
              <>
                {role && hasPermission("roles", "delete") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-rose-600"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="mr-1 h-3 w-3" /> Xóa
                  </Button>
                )}
                {role && hasPermission("roles", "update") && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs bg-slate-100"
                    onClick={() => navigate({ to: `/roles/${role.roleId}/edit` })}
                  >
                    <Settings className="mr-1 h-3 w-3" /> Sửa thông tin
                  </Button>
                )}
                {role && hasPermission("roles", "update") && (
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit className="mr-1 h-3 w-3" /> Sửa quyền
                  </Button>
                )}
                {role && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={handleExportMatrix}
                  >
                    <Download className="mr-1 h-3 w-3" /> Xuất Excel
                  </Button>
                )}
                {hasPermission("roles", "create") && (
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => navigate({ to: "/roles/new" })}
                  >
                    <Plus className="mr-1 h-3 w-3" /> Thêm vai trò
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Main: Sidebar + Matrix ── */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* ── Left: Roles Sidebar ── (điện thoại: xếp trên, cao tối đa 32vh, cuộn được) */}
          <aside className="flex flex-col w-full max-h-[32vh] border-b md:w-[200px] lg:w-[240px] md:max-h-none md:border-b-0 md:border-r border-slate-200 bg-white shrink-0">
            <div className="p-2 border-b border-slate-100 space-y-1.5">
              <div className="flex items-center justify-between px-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Vai trò ({filteredRoles.length}/{allRoles.length})
                </p>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                <Input
                  value={roleSearchTerm}
                  onChange={(e) => setRoleSearchTerm(e.target.value)}
                  placeholder="Tìm vai trò..."
                  className="h-8 pl-8 pr-7 text-xs bg-slate-50 border-slate-200 rounded-md focus-visible:ring-indigo-500"
                />
                {roleSearchTerm && (
                  <button
                    onClick={() => setRoleSearchTerm("")}
                    className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="py-1">
                {filteredRoles.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-400">
                    Không tìm thấy vai trò phù hợp
                  </div>
                ) : (
                  filteredRoles.map((r) => {
                  const isActive = r.roleId === selectedRoleId;
                  const isFullAccess = r.permissions && r.permissions["*"];
                  return (
                    <button
                      key={r.roleId}
                      className={cn(
                        "w-full flex items-center gap-2.5 px-3 py-2.5 mx-1 rounded-md text-xs transition-all cursor-pointer text-left",
                        isActive
                          ? "bg-indigo-600 text-white shadow-sm"
                          : "text-slate-700 hover:bg-slate-50",
                      )}
                      onClick={() => {
                        if (isEditing) {
                          toast.warning("Hãy lưu hoặc hủy chỉnh sửa trước");
                          return;
                        }
                        setSelectedRoleId(r.roleId);
                      }}
                    >
                      <div
                        className={cn(
                          "h-7 w-7 rounded-md flex items-center justify-center shrink-0",
                          isActive
                            ? "bg-white/20"
                            : isFullAccess
                              ? "bg-rose-50 text-rose-500"
                              : "bg-indigo-50 text-indigo-500",
                        )}
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div
                          className={cn(
                            "font-semibold truncate leading-tight",
                            isActive ? "text-white" : "text-slate-800",
                          )}
                        >
                          {r.name}
                        </div>
                        <div
                          className={cn(
                            "text-[10px] truncate",
                            isActive ? "text-indigo-200" : "text-slate-400",
                          )}
                        >
                          {isFullAccess
                            ? "Full Access"
                            : `${Object.keys(r.permissions || {}).length} modules`}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            </ScrollArea>

            {/* Role edit panel at bottom of sidebar */}
            {isEditing && role && (
              <div className="border-t border-slate-200 p-3 space-y-3 bg-white">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase font-bold text-slate-400">
                    Tên vai trò
                  </Label>
                  <Input
                    className="h-7 text-xs"
                    value={role.name}
                    onChange={(e) => setRole({ ...role, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase font-bold text-slate-400">
                    Mô tả
                  </Label>
                  <Textarea
                    className="text-xs min-h-[50px] resize-none"
                    value={role.description}
                    onChange={(e) =>
                      setRole({ ...role, description: e.target.value })
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-2 rounded-md bg-rose-50 border border-rose-100">
                  <div>
                    <p className="text-[11px] font-bold text-rose-700">
                      Full Access
                    </p>
                    <p className="text-[9px] text-rose-400">
                      Toàn quyền hệ thống
                    </p>
                  </div>
                  <Switch
                    checked={!!role.permissions?.["*"]}
                    onCheckedChange={handleFullAccessToggle}
                    className="data-[state=checked]:bg-rose-500 scale-90"
                  />
                </div>
              </div>
            )}
          </aside>

          {/* ── Right: Permission Matrix (full width) ── */}
          <div className="flex-1 overflow-auto bg-white">
            {!role || roleLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                {roleLoading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="h-12 w-12 mb-3 text-slate-300" />
                    <p className="text-sm">
                      Chọn một vai trò từ danh sách bên trái
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="w-full">
                <table className="w-full min-w-[720px] text-xs">
                  {/* Sticky header */}
                  <thead className="sticky top-0 z-20 bg-slate-50 border-b border-slate-200 shadow-sm">
                    <tr>
                      <th className="text-left font-bold text-slate-500 py-2 px-3 w-[180px] lg:w-[220px] uppercase text-[10px] tracking-wider">
                        Module
                      </th>
                      {PERM_ACTIONS.map((action) => {
                        const Icon = action.icon;
                        return (
                          <th
                            key={action.id}
                            className="text-center font-bold text-slate-500 py-2 px-1 w-[56px] lg:w-[68px]"
                          >
                            <div className="flex flex-col items-center gap-0.5">
                              <Icon className={cn("h-3 w-3", action.color)} />
                              <span className="text-[9px] uppercase tracking-tight">
                                {action.label}
                              </span>
                            </div>
                          </th>
                        );
                      })}
                      <th className="text-left font-bold text-slate-500 py-2 px-3 w-[120px] uppercase text-[10px] tracking-wider">
                        Phạm vi Dữ liệu
                      </th>
                      <th className="text-left font-bold text-slate-500 py-2 px-3 w-[120px] uppercase text-[10px] tracking-wider">
                        Phạm vi Tiền/Giá
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {MODULE_GROUPS.map((group) => {
                      const GroupIcon = group.icon;
                      return (
                        <Fragment key={group.group}>
                          {/* Group header row */}
                          <tr className="bg-slate-50 border-y border-slate-200 sticky top-[33px] z-10 shadow-sm">
                            <td
                              colSpan={PERM_ACTIONS.length + 1}
                              className="py-1.5 px-3"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className={cn(
                                    "h-5 w-5 rounded flex items-center justify-center text-white",
                                    group.color,
                                  )}
                                >
                                  <GroupIcon className="h-3 w-3" />
                                </div>
                                <span className="font-bold text-[11px] text-slate-700 uppercase tracking-wider">
                                  {group.group}
                                </span>
                              </div>
                            </td>
                          </tr>

                          {/* Module rows */}
                          {group.modules.map((mod) => (
                            <tr
                              key={mod.id}
                              className="border-b border-slate-100 hover:bg-indigo-50/20 transition-colors"
                            >
                              <td className="py-1.5 px-3 pl-10">
                                <span className="font-medium text-slate-700">
                                  {mod.name}
                                </span>
                              </td>
                              {PERM_ACTIONS.map((action) => {
                                const checked = hasPerm(mod.id, action.id);
                                const isAll = action.id === "all";
                                const disabled =
                                  !isEditing || !!role.permissions?.["*"];

                                return (
                                  <td
                                    key={action.id}
                                    className="text-center py-1.5 px-1"
                                  >
                                    <div className="flex justify-center">
                                      <Checkbox
                                        checked={checked}
                                        onCheckedChange={() =>
                                          togglePerm(mod.id, action.id)
                                        }
                                        disabled={disabled}
                                        className={cn(
                                          "h-4 w-4 rounded-[3px] border-slate-300 transition-colors",
                                          checked &&
                                            isAll &&
                                            "data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600",
                                          checked &&
                                            !isAll &&
                                            "data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600",
                                          disabled &&
                                            "opacity-60 cursor-not-allowed",
                                        )}
                                      />
                                    </div>
                                  </td>
                                );
                              })}
                              <td className="py-1.5 px-3">
                                <Select
                                  disabled={
                                    !isEditing || !!role.permissions?.["*"]
                                  }
                                  value={
                                    role.permissions?.[mod.id]?.dataScope ||
                                    (role.permissions?.["*"] ? "ALL" : "NONE")
                                  }
                                  onValueChange={(val) =>
                                    updateScope(mod.id, "dataScope", val)
                                  }
                                >
                                  <SelectTrigger className="h-7 text-[11px]">
                                    <SelectValue placeholder="Chọn..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="NONE">
                                      Không được phép
                                    </SelectItem>
                                    <SelectItem value="PERSONAL">
                                      Chỉ của mình
                                    </SelectItem>
                                    <SelectItem value="TEAM">
                                      Của Team
                                    </SelectItem>
                                    <SelectItem value="ALL">Tất cả</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-1.5 px-3">
                                <Select
                                  disabled={
                                    !isEditing || !!role.permissions?.["*"]
                                  }
                                  value={
                                    role.permissions?.[mod.id]
                                      ?.financialScope ||
                                    (role.permissions?.["*"] ? "ALL" : "NONE")
                                  }
                                  onValueChange={(val) =>
                                    updateScope(mod.id, "financialScope", val)
                                  }
                                >
                                  <SelectTrigger className="h-7 text-[11px]">
                                    <SelectValue placeholder="Chọn..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="NONE">
                                      Không được xem
                                    </SelectItem>
                                    <SelectItem value="PERSONAL">
                                      Chỉ của mình
                                    </SelectItem>
                                    <SelectItem value="TEAM">
                                      Của Team
                                    </SelectItem>
                                    <SelectItem value="DEPARTMENT">
                                      Phòng ban
                                    </SelectItem>
                                    <SelectItem value="ALL">Tất cả</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* ── Delete Alert ── */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa vai trò</AlertDialogTitle>
            <AlertDialogDescription>
              Hành động này không thể hoàn tác. Vai trò "{role?.name}" sẽ bị xóa
              vĩnh viễn.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}{" "}
              Xóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
